import { MobileLayout } from '@/components/mobile/MobileLayout';
import { MobileWizardDialog } from '@/components/mobile/MobileWizardDialog';
import { useWorkflow } from '@/contexts/WorkflowContext';
import { useSelectionMode } from '@/contexts/SelectionModeContext';
import { useDialogs } from '@/contexts/DialogContext';
import type { WorkflowBlock, BlockGroup, BlockType, PortSide } from '@/types/workflow';
import type { ConnectionDebugInfo } from '@/hooks/useDocuments';
import type { RefObject } from 'react';

export interface IndexMobileShellProps {
  // Selection — selectedGroup is derived in caller (groups.find(g => g.id === ...))
  // and not stored in workflow state, so it stays as a prop.
  selectedGroup: BlockGroup | null;

  // Document / connection / a11y
  isConnected: boolean;
  connectionDebugInfo?: ConnectionDebugInfo;
  isDirty: boolean;
  announcement: string;
  canUndo: boolean;
  canRedo: boolean;
  canvasInnerRef: RefObject<HTMLDivElement>;

  /** Mobile gesture-handlers passed through to WorkflowCanvas. */
  mobileGestures?: import('@/components/editor/WorkflowCanvas').MobileGestureHandlers;

  // Handlers (wrapped versions; raw mutations come from context internally)
  onSelectBlock: (id: string) => void;
  onSelectGroup: (id: string | null) => void;
  onCanvasClick: () => void;
  onCreateBlock: (type: BlockType, x: number, y: number) => WorkflowBlock;
  onUpdateBlockIcon: (id: string, iconKey: string) => void;
  onConnect: (fromId: string, toId: string, fromPort?: PortSide, toPort?: PortSide) => void;
  onToggleGroupSelection: (id: string) => void;
  onToggleConnectMode: () => void;
  onToggleGroupMode: () => void;
  onUpdateGroupPosition: (id: string, x: number, y: number) => void;
  onConvertToLayer: (id: string) => void;
  onNavigateToLayer: (groupId: string) => void;
  onDeleteBlock: () => void;
  onDeleteGroup: () => void;
  onSave: () => void;
  onLoad: () => void;
  onExportPNG: () => void;
  onSimulate: () => void;
  onClear: () => void;
  onImportJson: () => void;
  onCopyJson: () => void;
  onHelp: () => void;
  onAiJson: () => void;
  onAiAssistant: () => void;
  onPreview: () => void;
  onLogin: () => void;
  onCloudWorkspace: () => void;
  onShareWorkspace: () => void;
  onFeatureRequest: () => void;
  onBugReport: () => void;
  onDonate: () => void;
  onUndo: () => void;
  onRedo: () => void;
  onWizardComplete: (...args: unknown[]) => void;
}

/**
 * Mobile-branch shell. Reads workflow state + raw mutations från
 * WorkflowContext — wrappade handlers (onConnect, onSelectBlock etc.)
 * kommer in som props från Index.tsx eftersom de innehåller routing/
 * logging som hooken inte känner till.
 */
export function IndexMobileShell(props: IndexMobileShellProps) {
  const { onWizardComplete, ...rest } = props;

  // Workflow data + raw mutations the mobile layout needs.
  const wf = useWorkflow();
  const { selectedGroupId, connectMode, groupMode, groupSelection } = useSelectionMode();
  const dlg = useDialogs();
  const isWizardOpen = dlg.wizardOpen;

  return (
    <>
      {!isWizardOpen && (
        <MobileLayout
          {...rest}
          selectedGroupId={selectedGroupId}
          connectMode={connectMode}
          groupMode={groupMode}
          groupSelection={groupSelection}
          blocks={wf.blocks}
          connectors={wf.connectors}
          groups={wf.groups}
          selectedBlock={wf.selectedBlock}
          selectedBlockId={wf.selectedBlockId}
          workflowName={wf.workflowName}
          visibleBlocks={wf.visibleBlocks}
          visibleConnectors={wf.visibleConnectors}
          collapsedGroups={wf.collapsedGroups}
          expandedGroups={wf.expandedGroups}
          breadcrumbs={wf.breadcrumbs}
          layerPath={wf.layerPath}
          currentLayerGroupId={wf.currentLayerGroupId}
          onUpdateBlockPosition={wf.updateBlockPosition}
          onUpdateConnectorWaypoints={wf.updateConnectorWaypoints}
          onUpdateConnectorLabel={wf.updateConnectorLabel}
          onDeleteConnector={wf.deleteConnector}
          onNavigateInto={wf.navigateInto}
          onNavigateToLevel={wf.navigateToLevel}
          onSetSelectedBlockId={wf.setSelectedBlockId}
          onUpdateBlock={wf.updateBlock}
          onUpdateGroup={wf.updateGroup}
          onUpdateWorkflowName={wf.setWorkflowName}
        />
      )}
      <MobileWizardDialog open={dlg.wizardOpen} onComplete={onWizardComplete} />
    </>
  );
}
